/**
 * Session primitives, ported from apps/web (utils/session.ts,
 * router/publicRoutes.ts, utils/supportSession.ts).
 *
 * The session cookie is HttpOnly, so the app cannot read it. `lemon.session`
 * in localStorage is a HINT that a server session exists: with it, a normal
 * navigation skips the probe; without it, the session is resolved with exactly
 * one `GET /api/user/me`. It is never an authorisation decision — the API is
 * the only authority. The key is shared with apps/web on purpose: both apps
 * speak for the same cookie.
 */
import { readStored, removeStored, writeStored } from '@/lib/storage';

const SESSION_HINT_KEY = 'lemon.session';
const SUPPORT_SESSION_KEY = 'lemon.support_session';

/** Where a signed-in user lands when no destination was asked for. */
export const DEFAULT_AUTHENTICATED_PATH = '/performance';

export const hasSessionHint = (): boolean => readStored(SESSION_HINT_KEY) === '1';
export const markSessionActive = (): void => writeStored(SESSION_HINT_KEY, '1');
export const clearSessionHint = (): void => removeStored(SESSION_HINT_KEY);

/**
 * Per-tab marker (sessionStorage) that this tab is an administrator's support
 * session. Set before the first product screen renders so analytics can stay
 * off; the server's `support_session` on /api/user/me drives the UI.
 */
export const markSupportSessionTab = (): void => writeStored(SUPPORT_SESSION_KEY, '1', 'session');
export const isSupportSessionTab = (): boolean => readStored(SUPPORT_SESSION_KEY, 'session') === '1';
export const clearSupportSessionTab = (): void => removeStored(SUPPORT_SESSION_KEY, 'session');

/** Screens a signed-out visitor may reach. The 404 page is NOT public. */
export const PUBLIC_AUTH_PATHS: ReadonlySet<string> = new Set([
  '/login',
  '/signup',
  '/auth',
  '/forgot-password',
  '/reset',
  '/support-session',
]);

export const isPublicAuthPath = (pathname: string): boolean => {
  const normalized = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  return PUBLIC_AUTH_PATHS.has(normalized);
};

/**
 * Reduce an attempted destination to a same-origin path this app may navigate
 * to after sign-in. Rejects absolute and scheme-bearing URLs, protocol-relative
 * `//host`, `/\host` (protocol-relative to some parsers), and `/login…`
 * (which would loop).
 */
export const safeRedirectTarget = (value: unknown): string | null => {
  if (typeof value !== 'string' || value.length === 0) return null;
  if (!value.startsWith('/')) return null;
  if (value.startsWith('//') || value.startsWith('/\\')) return null;
  if (value.startsWith('/login')) return null;
  return value;
};

/**
 * The sign-in URL that brings the user back here afterwards. The default
 * landing path carries no `redirect` (it is where sign-in goes anyway).
 */
export const loginPathFor = (attempted: string): string => {
  const safe = safeRedirectTarget(attempted);
  if (!safe || safe === DEFAULT_AUTHENTICATED_PATH) return '/login';
  return `/login?redirect=${encodeURIComponent(safe)}`;
};
